import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { css } from '@emotion/css';
import { message, Select, Tag } from 'antd';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { getStoreCount, StoreScopes } from 'components/StoreScopes';
import { resolveProductStoreScopes } from '../../api/store';
import type { ProductStoreScope } from '../../model/types';

export interface StoreScopePickerSelectProps {
  disabled?: boolean;
  /** 解析完成后的平台/区域/店铺范围；空数组表示全部店铺 */
  onChange: (storeScopes: ProductStoreScope[]) => void;
}

const selectClassName = css`
  min-width: 160px;
  max-width: 200px;
`;

const storeScopesFormClassName = css`
  max-height: 95vh;
  overflow-y: auto;
`;

const storeDropdownClassName = css`
  width: 950px !important;
  overflow-y: auto;
  padding-top: 0;
  padding-bottom: 0;
  color: rgba(0, 0, 0, 0.85);
`;

/** 商品选择器内的店铺筛选入口（对齐 perseus ProductStorePickerButton：选店后回第一页） */
export function StoreScopePickerSelect({
  disabled = false,
  onChange,
}: StoreScopePickerSelectProps) {
  const { t } = useTranslate();
  const [storeSelection, setStoreSelection] = useState<General.StoreScope[]>([]);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [resolving, setResolving] = useState(false);
  const selectedStoreCount = getStoreCount(storeSelection);

  const applyScopes = async (scopes: General.StoreScope[]) => {
    setPickerVisible(false);
    setResolving(true);
    try {
      onChange(await resolveProductStoreScopes(scopes));
      setStoreSelection(scopes);
    } catch (error) {
      message.error(
        describeKnowledgeApiError(
          error,
          undefined,
          t('productKnowledge_overview_store_scope_failed'),
        ),
      );
    } finally {
      setResolving(false);
    }
  };

  return (
    <Select
      mode="multiple"
      showSearch={false}
      aria-label={t('productKnowledge_filter_store_select')}
      className={selectClassName}
      placeholder={t('productKnowledge_filter_store_select')}
      value={selectedStoreCount ? ['placeholder'] : []}
      allowClear
      loading={resolving}
      disabled={disabled || resolving}
      onClear={() => void applyScopes([])}
      tagRender={() => (
        <Tag>{t('productKnowledge_filter_selected_stores', { count: selectedStoreCount })}</Tag>
      )}
      open={pickerVisible}
      onDropdownVisibleChange={setPickerVisible}
      dropdownRender={() =>
        pickerVisible ? (
          <StoreScopes
            allowEmpty
            supportWa={false}
            showFooterTips={false}
            className={storeScopesFormClassName}
            value={storeSelection}
            onCancel={() => setPickerVisible(false)}
            onChange={(scopes) => void applyScopes(scopes)}
          />
        ) : (
          <></>
        )
      }
      dropdownClassName={storeDropdownClassName}
    />
  );
}
